import "core-js/modules/es.array.reduce.js";
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/esm/extends';
import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { usePopper } from 'react-popper';

function useGetLatest(val) {
  var ref = useRef(val);
  ref.current = val;
  return useCallback(function () {
    return ref.current;
  }, []);
}

var noop = function noop() {// do nothing
};

function useControlledState(_ref) {
  var initial = _ref.initial,
      value = _ref.value,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange;

  if (initial === undefined && value === undefined) {
    throw new TypeError('Either "value" or "initial" variable must be set. Now both are undefined');
  }

  var _React$useState = useState(initial),
      state = _React$useState[0],
      setState = _React$useState[1];

  var getLatest = useGetLatest(state);
  var set = useCallback(function (updater) {
    var state = getLatest();
    var updatedState = typeof updater === 'function' ? updater(state) : updater;
    if (typeof updatedState.persist === 'function') updatedState.persist();
    setState(updatedState);
    if (typeof onChange === 'function') onChange(updatedState);
  }, [getLatest, onChange]);
  var isControlled = value !== undefined;
  return [isControlled ? value : state, isControlled ? onChange : set];
}

function generateBoundingClientRect(x, y) {
  if (x === void 0) {
    x = 0;
  }

  if (y === void 0) {
    y = 0;
  }

  return function () {
    return {
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x
    };
  };
} // pageX cannot be supplied in the tests, so we fallback to clientX
// @see https://github.com/testing-library/dom-testing-library/issues/144


var mouseOutsideRect = function mouseOutsideRect(_ref2, _ref3) {
  var clientX = _ref2.clientX,
      clientY = _ref2.clientY;
  var bottom = _ref3.bottom,
      left = _ref3.left,
      right = _ref3.right,
      top = _ref3.top; // DOMRect contains fractional pixel values but MouseEvent reports integers,
  // so we round DOMRect boundaries to make DOMRect slightly bigger.
  // Also exceed the DOMRect by 1 pixel to fix Chromium reporting MouseEvent's
  // `clientX` and `clientY` by the whole integer away from the DOMRect.
  // see https://github.com/mohsinulhaq/react-popper-tooltip/issues/118#issuecomment-782698921

  return clientX < Math.floor(left) - 1 || clientX > Math.ceil(right) + 1 || clientY < Math.floor(top) - 1 || clientY > Math.ceil(bottom) + 1;
};
/**
 * Checks if mouseevent is triggered outside triggerRef and tooltipRef.
 * Counts with potential offset between them.
 * @param {MouseEvent} mouseEvent
 * @param {HTMLElement} triggerRef
 * @param {HTMLElement} tooltipRef - provide only when prop `interactive` is on
 */


function isMouseOutside(mouseEvent, triggerRef, tooltipRef) {
  var triggerRect = triggerRef.getBoundingClientRect();
  if (!tooltipRef) return mouseOutsideRect(mouseEvent, triggerRect);
  var tooltipRect = tooltipRef.getBoundingClientRect(); // triggerRect extended to the tooltipRect boundary, thus will contain cursor
  // moving from triggerRect to tooltipRect over some non zero offset.

  var triggerRectExtendedToTooltip = {
    bottom: Math.max(triggerRect.bottom, tooltipRect.top),
    left: Math.min(triggerRect.left, tooltipRect.right),
    right: Math.max(triggerRect.right, tooltipRect.left),
    top: Math.min(triggerRect.top, tooltipRect.bottom)
  };
  return mouseOutsideRect(mouseEvent, triggerRectExtendedToTooltip) && mouseOutsideRect(mouseEvent, tooltipRect);
}

var isArray = Array.isArray;
var virtualElement = {
  getBoundingClientRect: generateBoundingClientRect()
};
var defaultConfig = {
  closeOnOutsideClick: true,
  closeOnTriggerHidden: false,
  defaultVisible: false,
  delayHide: 0,
  delayShow: 0,
  followCursor: false,
  interactive: false,
  mutationObserverOptions: {
    attributes: true,
    childList: true,
    subtree: true
  },
  offset: [0, 6],
  trigger: 'hover'
};

function usePopperTooltip(config, popperOptions) {
  var _popperProps$state, _popperProps$state$mo, _popperProps$state$mo2;

  if (config === void 0) {
    config = {};
  }

  if (popperOptions === void 0) {
    popperOptions = {};
  } // Merging options with default options.
  // Keys with undefined values are replaced with the default ones if any.
  // Keys with other values pass through.


  var finalConfig = Object.keys(defaultConfig).reduce(function (config, key) {
    var _extends2;

    return _extends({}, config, (_extends2 = {}, _extends2[key] = config[key] !== undefined ? config[key] : defaultConfig[key], _extends2));
  }, config);
  var defaultModifiers = useMemo(function () {
    return [{
      name: 'offset',
      options: {
        offset: finalConfig.offset
      }
    }];
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  isArray(finalConfig.offset) ? finalConfig.offset : []);

  var finalPopperOptions = _extends({}, popperOptions, {
    placement: popperOptions.placement || finalConfig.placement,
    modifiers: popperOptions.modifiers || defaultModifiers
  });

  var _React$useState = useState(null),
      triggerRef = _React$useState[0],
      setTriggerRef = _React$useState[1];

  var _React$useState2 = useState(null),
      tooltipRef = _React$useState2[0],
      setTooltipRef = _React$useState2[1];

  var _useControlledState = useControlledState({
    initial: finalConfig.defaultVisible,
    value: finalConfig.visible,
    onChange: finalConfig.onVisibleChange
  }),
      visible = _useControlledState[0],
      setVisible = _useControlledState[1];

  var timer = useRef();
  useEffect(function () {
    return function () {
      return clearTimeout(timer.current);
    };
  });

  var _usePopper = usePopper(finalConfig.followCursor ? virtualElement : triggerRef, tooltipRef, finalPopperOptions),
      styles = _usePopper.styles,
      attributes = _usePopper.attributes,
      popperProps = _objectWithoutPropertiesLoose(_usePopper, ["styles", "attributes"]);

  var update = popperProps.update;
  var getLatest = useGetLatest({
    visible: visible,
    triggerRef: triggerRef,
    tooltipRef: tooltipRef,
    finalConfig: finalConfig
  });
  var isTriggeredBy = useCallback(function (trigger) {
    return isArray(finalConfig.trigger) ? finalConfig.trigger.includes(trigger) : finalConfig.trigger === trigger;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  isArray(finalConfig.trigger) ? finalConfig.trigger : [finalConfig.trigger]);
  var hideTooltip = useCallback(function () {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(function () {
      return setVisible(false);
    }, finalConfig.delayHide);
  }, [finalConfig.delayHide, setVisible]);
  var showTooltip = useCallback(function () {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(function () {
      return setVisible(true);
    }, finalConfig.delayShow);
  }, [finalConfig.delayShow, setVisible]);
  var toggleTooltip = useCallback(function () {
    if (getLatest().visible) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [getLatest, hideTooltip, showTooltip]); // Handle click outside

  useEffect(function () {
    if (!getLatest().finalConfig.closeOnOutsideClick) return;

    var handleClickOutside = function handleClickOutside(event) {
      var _getLatest = getLatest(),
          tooltipRef = _getLatest.tooltipRef,
          triggerRef = _getLatest.triggerRef;

      var target = event.target;

      if (target instanceof Node) {
        if (tooltipRef != null && triggerRef != null && !tooltipRef.contains(target) && !triggerRef.contains(target)) {
          hideTooltip();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return function () {
      return document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [getLatest, hideTooltip]); // Trigger: click

  useEffect(function () {
    if (triggerRef == null || !isTriggeredBy('click')) return;
    triggerRef.addEventListener('click', toggleTooltip);
    return function () {
      return triggerRef.removeEventListener('click', toggleTooltip);
    };
  }, [triggerRef, isTriggeredBy, toggleTooltip]); // Trigger: right-click

  useEffect(function () {
    if (triggerRef == null || !isTriggeredBy('right-click')) return;

    var preventDefaultAndToggle = function preventDefaultAndToggle(event) {
      // Don't show the context menu
      event.preventDefault();
      toggleTooltip();
    };

    triggerRef.addEventListener('contextmenu', preventDefaultAndToggle);
    return function () {
      return triggerRef.removeEventListener('contextmenu', preventDefaultAndToggle);
    };
  }, [triggerRef, isTriggeredBy, toggleTooltip]); // Trigger: focus

  useEffect(function () {
    if (triggerRef == null || !isTriggeredBy('focus')) return;
    triggerRef.addEventListener('focus', showTooltip);
    triggerRef.addEventListener('blur', hideTooltip);
    return function () {
      triggerRef.removeEventListener('focus', showTooltip);
      triggerRef.removeEventListener('blur', hideTooltip);
    };
  }, [triggerRef, isTriggeredBy, showTooltip, hideTooltip]); // Trigger: hover on trigger

  useEffect(function () {
    if (triggerRef == null || !isTriggeredBy('hover')) return;
    triggerRef.addEventListener('mouseenter', showTooltip);
    var stopTimer;

    if (!visible) {
      stopTimer = function stopTimer() {
        return clearTimeout(timer.current);
      };

      triggerRef.addEventListener('mouseleave', stopTimer);
    }

    return function () {
      triggerRef.removeEventListener('mouseenter', showTooltip);

      if (stopTimer) {
        triggerRef.removeEventListener('mouseleave', stopTimer);
      }
    };
  }, [isTriggeredBy, hideTooltip, showTooltip, triggerRef, visible]); // Listen for mouse exiting the hover area &&
  // handle the followCursor

  useEffect(function () {
    if (!visible || triggerRef == null || !isTriggeredBy('hover') && !finalConfig.followCursor) {
      return;
    }

    var lastMouseOutside = false;

    var handleMouseMove = function handleMouseMove(event) {
      var mouseOutside = isMouseOutside(event, triggerRef, !finalConfig.followCursor && getLatest().finalConfig.interactive && tooltipRef);

      if (mouseOutside && lastMouseOutside !== mouseOutside) {
        hideTooltip();
      }

      if (!mouseOutside && finalConfig.followCursor) {
        virtualElement.getBoundingClientRect = generateBoundingClientRect(event.clientX, event.clientY);
        update == null ? void 0 : update();
      }

      lastMouseOutside = mouseOutside;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return function () {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [finalConfig.followCursor, getLatest, hideTooltip, isTriggeredBy, tooltipRef, triggerRef, update, visible]); // Trigger: hover on tooltip, keep it open if hovered

  useEffect(function () {
    if (tooltipRef == null || !getLatest().finalConfig.interactive) return;
    tooltipRef.addEventListener('mouseenter', showTooltip);
    tooltipRef.addEventListener('mouseleave', hideTooltip);
    return function () {
      tooltipRef.removeEventListener('mouseenter', showTooltip);
      tooltipRef.removeEventListener('mouseleave', hideTooltip);
    };
  }, [tooltipRef, showTooltip, hideTooltip, getLatest]); // Handle closing tooltip if trigger hidden

  var isReferenceHidden = popperProps == null ? void 0 : (_popperProps$state = popperProps.state) == null ? void 0 : (_popperProps$state$mo = _popperProps$state.modifiersData) == null ? void 0 : (_popperProps$state$mo2 = _popperProps$state$mo.hide) == null ? void 0 : _popperProps$state$mo2.isReferenceHidden;
  useEffect(function () {
    if (finalConfig.closeOnTriggerHidden && isReferenceHidden) hideTooltip();
  }, [finalConfig.closeOnTriggerHidden, hideTooltip, isReferenceHidden]); // Handle tooltip DOM mutation changes (aka mutation observer)

  useEffect(function () {
    if (tooltipRef == null || update == null || finalConfig.mutationObserverOptions == null) return;
    var observer = new MutationObserver(update);
    observer.observe(tooltipRef, finalConfig.mutationObserverOptions);
    return function () {
      return observer.disconnect();
    };
  }, [finalConfig.mutationObserverOptions, tooltipRef, update]); // Tooltip props getter

  var getTooltipProps = function getTooltipProps(args) {
    if (args === void 0) {
      args = {};
    }

    return _extends({}, args, {
      style: _extends({}, args.style, styles.popper)
    }, attributes.popper);
  }; // Arrow props getter


  var getArrowProps = function getArrowProps(args) {
    if (args === void 0) {
      args = {};
    }

    return _extends({}, args, attributes.arrow, {
      style: _extends({}, args.style, styles.arrow),
      'data-popper-arrow': true
    });
  };

  return _extends({
    getArrowProps: getArrowProps,
    getTooltipProps: getTooltipProps,
    setTooltipRef: setTooltipRef,
    setTriggerRef: setTriggerRef,
    tooltipRef: tooltipRef,
    triggerRef: triggerRef,
    visible: visible
  }, popperProps);
}

export { usePopperTooltip };